<template>
    <div class="card flex-grow-1">
        <div v-show="panel.header !== ''" class="card-header">
            <p class="h5 mb-0 panel-header">{{ panel.header }}</p>
        </div>
        <div class="card-body panel-card-body d-flex flex-column global-theme-text">
            <div class="row mt-2" style="min-height: 360px">
                <div class="col-12">
                    <div class="alert alert-primary border-0 mb-0">{{ questionSubmitted }}</div>
                    <hr class="my-3" />
                    <div class="alert alert-secondary border-0">
                        <div v-show="avatarThinking">
                            <div class="d-flex align-items-center chat-dot-container">
                                <div class="chat-dot chat-dot-1"></div>
                                <div class="chat-dot chat-dot-2"></div>
                                <div class="chat-dot chat-dot-3"></div>
                            </div>
                        </div>
                        <div v-show="!avatarThinking">
                            {{ questionResponse }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="card-footer">
            <div class="d-flex align-items-center" style="margin-top: 6px; margin-bottom: 6px">
                <div class="flex-grow-1 mr-1">
                    <input
                        type="text"
                        class="form-control chat-input"
                        :placeholder="textInputPlaceholder"
                        :aria-label="textInputPlaceholder"
                        v-model="questionText"
                        v-on:keyup.enter="submitChatQuestion()"
                        maxlength="255"
                        :disabled="locked"
                    />
                </div>
                <div class="flex-grow-0 flex-shrink-0">
                    <div
                        class="icon icon-glyphicons-basic-circle-up chat-submit-btn align-middle"
                        :class="{'chat-submit-btn-disabled': locked}"
                        @click="submitChatQuestion()"
                        role="button"
                    ></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import http from "@/axios.js"

export default {
    name: "ChatPanel",
    props: {
        panel: {
            type: Object,
            required: true,
        },
        sessionId: {
            type: [String, Number],
            required: true,
        },
        savedResponses: {
            type: Array,
            required: false,
        },
        locked: {
            type: Boolean,
            required: false
        },
    },
    data() {
        return {
            questionText: "",
            questionSubmitted: "",
            questionResponse: "",
            avatarThinking: false,
            correctIntentIds: [],
        }
    },
    computed: {
        correctIntents() {
            return this.correctIntentIds.length
        },
        panelID() {
            return this.panel.id
        },
        textInputPlaceholder() {
            if (this.panel.chat_input_placeholder) {
                return this.panel.chat_input_placeholder
            }
            return "Ask a question here"
        },
        subheaderIsConfigured() {
            return this.panel.sub_header !== "" && this.panel.sub_header != "<p></p>"
        },
        savedResponseCount() {
            return this.savedResponses ? this.savedResponses.length : 0
        },
        isSubmitted() {
            return this.savedResponseCount > 0
        },
    },
    watch: {
        correctIntents(val) {
            this.$emit("update:correctResponsesCount", val)
        },
        panelID() {
            this.initPanel()
        },
        savedResponseCount() {
            this.populateSavedResponses()
        },
        isSubmitted(value) {
            this.$emit("update:isSubmitted", value)
        },
    },
    methods: {
        // Populate previously discovered intents for the panel.
        populateSavedResponses() {
            if (this.savedResponses && this.savedResponses.length) {
                let vm = this
                this.savedResponses.forEach(function (intentDiscovered) {
                    let panelIntent = vm.panel.chat_intents.find(
                        (panelIntent) => panelIntent.id == intentDiscovered.chat_agent_intent.id
                    )
                    if (panelIntent) {
                        // Set the feedback message and correctness on the intent.
                        vm.$set(panelIntent, "message", intentDiscovered.message)
                        vm.$set(panelIntent, "correct", intentDiscovered.correct)
                        vm.$set(panelIntent, "was_missed", intentDiscovered.was_missed)
                        vm.$set(panelIntent, "feedback_message", intentDiscovered.chat_agent_intent.feedback_message)
                    }
                    if (intentDiscovered.correct) {
                        vm.correctIntentIds.push(intentDiscovered.chat_agent_intent.id)
                    }
                })
                this.$emit("update:isSubmitted", true)
            } else {
                this.$emit("update:isSubmitted", false)
            }
        },
        // Submit a chat question to a chat agent.
        submitChatQuestion() {
            if (!this.questionText || this.locked) {
                return
            }
            this.avatarThinking = true
            this.questionSubmitted = this.questionText
            this.questionResponse = "..."
            this.questionText = ""
            let vm = this
            http.post(`/api/students/sessions/${this.sessionId}/panel/${this.panel.id}/askagent/`, {
                question_text: this.questionSubmitted,
            })
                .then(function (response) {
                    vm.questionResponse = response.data.avatar_response

                    response.data.intents_discovered.forEach(function (intentDiscovered) {
                        // Check for the existence of a matched (configured) intent.
                        if (intentDiscovered.chat_agent_intent && intentDiscovered.chat_agent_intent.id) {
                            let panelIntent = vm.panel.chat_intents.find(
                                (panelIntent) => panelIntent.id == intentDiscovered.chat_agent_intent.id
                            )
                            // Set the feedback message and correctness on the intent.
                            vm.$set(panelIntent, "message", intentDiscovered.message)
                            vm.$set(panelIntent, "feedback_message", intentDiscovered.chat_agent_intent.feedback_message)
                            vm.$set(panelIntent, "correct", intentDiscovered.correct)
                            // Ensure the was_missed flag is set to false, in case this intent is currently
                            // displayed in the 'missed' state to the user.
                            if (intentDiscovered.correct === true) {
                                vm.$set(panelIntent, "was_missed", false)
                            }
                        }

                        // Track any correct intents.
                        if (
                            intentDiscovered.correct &&
                            !vm.correctIntentIds.includes(intentDiscovered.chat_agent_intent.id)
                        ) {
                            vm.correctIntentIds.push(intentDiscovered.chat_agent_intent.id)
                        }

                        vm.$emit("response-received")
                    })
                })
                .finally(function () {
                    vm.avatarThinking = false
                })
        },
        initPanel() {
            this.correctIntentIds = []
            this.questionSubmitted = this.panel.chat_initial_message || "Your questions will appear here."
            this.questionResponse = this.panel.chat_initial_response || "Responses will appear here."
        },
    },
    mounted() {
        this.initPanel()
        this.populateSavedResponses()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/chat.scss";

.chat-container {
    @media (min-width: $breakpoint-session) {
        height: 100%;
    }
}

.chat-col-left {
    padding-right: 0rem;
    @media (min-width: $breakpoint-session) {
        padding-right: 0.4rem !important;
    }
}

.chat-col-right {
    padding-left: 0rem;
    @media (min-width: $breakpoint-session) {
        padding-left: 0.4rem !important;
    }
}

.chat-col {
    padding-right: 0rem;
    padding-left: 0rem;
}
</style>